import { useQuery } from '@tanstack/react-query';
import Highcharts from 'highcharts';
import 'highcharts/modules/exporting';
import 'highcharts/modules/series-label';
import 'highcharts/highcharts-more';
import HighchartsReact from 'highcharts-react-official';
import type { TFunction } from 'i18next';
import { merge } from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ROLE } from '@/common/role';
import type { TpsSectionChartRecord } from '@/common/sensors/tps/tps-section-chart';
import { Card, CardContent } from '@/components/ui/card';
import { useTheme } from '@/context/ThemeContext';
import { usePermission } from '@/hooks/use-permission';
import { HIGH_CHART_DARK_THEME, HIGH_CHART_LIGHT_THEME } from '@/lib/highchart/setup';
import { useQueryGetTpsSectionChart } from '@/queries/assets/sensors/tps/get-section-chart';
import { AbsoluteLoader } from '../../absoluteLoader';
import { AccessDeniedView } from '../AccessDeniedView';
import { EmptyChart } from '../EmptyChart';

const SOLL_ANGLE = 540 - 1e-6; // 180° im 180..540 Setup
const SOLL_RADIUS = 10;
const BAU_ANGLE = 450; // 90° -> 450
const BAU_RADIUS = 12;
const R_SOLL_MM = 10.0;

/**
 * Implements the fmod (floating-point remainder) function in JavaScript.
 * The result has the same sign as 'a' (dividend).
 * @param {number} a The dividend.
 * @param {number} b The divisor.
 * @returns {number} The floating-point remainder.
 */
function fmod(a: number, b: number): number {
	return Number((a - Math.floor(a / b) * b).toPrecision(8));
}

function normAngleDeg(a: number): number {
	a = fmod(a, 360.0);
	return a < 0 ? a + 360.0 : a;
}

function circleFitLs(pts: TpsSectionChartRecord[]) {
	const n = pts.length;
	if (n < 3) {
		const cx = pts.reduce((sum, p) => sum + p.x, 0) / n;
		const cy = pts.reduce((sum, p) => sum + p.y, 0) / n;
		const rFit = pts.reduce((sum, p) => sum + Math.hypot(p.x - cx, p.y - cy), 0);
		return { cx, cy, rFit };
	}
	const sums = pts.reduce(
		(acc, p) => {
			const x = p.x;
			const y = p.y;
			const z = x * x + y * y;
			acc.sx += x;
			acc.sy += y;
			acc.sxx += x * x;
			acc.syy += y * y;
			acc.sxy += x * y;
			acc.sz += z;
			acc.sxz += z * x;
			acc.syz += z * y;
			return acc;
		},
		{ sx: 0, sy: 0, sxx: 0, syy: 0, sxy: 0, sz: 0, sxz: 0, syz: 0 },
	);
	const { sx, sy, sxx, syy, sxy, sz, sxz, syz } = sums;
	const m = [
		[sxx, sxy, sx],
		[sxy, syy, sy],
		[sx, sy, n],
	];
	const b = [-sxz, -syz, -sz];

	for (let i = 0; i < 3; i++) {
		let pivot = i;
		for (let r = i + 1; r < 3; r++) {
			if (Math.abs(m[r][i]) > Math.abs(m[pivot][i])) {
				pivot = r;
			}
		}
		if (Math.abs(m[pivot][i]) < 1e-12) {
			const mx = sx / n;
			const my = sy / n;
			const r = pts.reduce((acc, p) => {
				acc += Math.hypot(p.x - mx, p.y - my);
				return acc;
			}, 0);
			return { cx: mx, cy: my, rFit: r / n };
		}
		if (pivot !== i) {
			[m[i], m[pivot]] = [m[pivot], m[i]];
			[b[i], b[pivot]] = [b[pivot], b[i]];
		}
		const div = m[i][i];
		for (let c = i; c < 3; c++) {
			m[i][c] /= div;
		}
		b[i] /= div;
		for (let r = i + 1; r < 3; r++) {
			const f = m[r][i];
			for (let c = i; c < 3; c++) {
				m[r][c] -= f * m[i][c];
			}
			b[r] -= f * b[i];
		}
	}
	const xv = [0, 0, 0];
	for (let i = 2; i >= 0; i--) {
		let sum = b[i];
		for (let c = i + 1; c < 3; c++) {
			sum -= m[i][c] * xv[c];
		}
		xv[i] = sum;
	}
	const [xva, xvb, xvc] = xv;
	const cx = -xva / 2.0;
	const cy = -xvb / 2.0;
	const r2 = cx * cx + cy * cy - xvc;
	return { cx, cy, rFit: Math.sqrt(r2 || 0) };
}

function buildSeriesForGroup(byPoint: Record<number, TpsSectionChartRecord[]>, start: number, end: number) {
	const rows: TpsSectionChartRecord[] = [];
	for (let p = start; p <= end; p++) {
		if (!byPoint[p]) continue;
		rows.push(...byPoint[p]);
	}
	if (rows.length === 0) {
		return { series: [], center: [0, 0], rfit: 0 };
	}
	// Kreisfit auf Rohpunkten
	const { cx, cy, rFit } = circleFitLs(rows);

	const series: Array<{
		name: string | null;
		x: number;
		y: number | null;
		dx: number;
		dy: number;
	}> = [];
	rows.forEach((r) => {
		const x0 = r.x - cx;
		const y0 = r.y - cy;
		// 1) Winkel aus zentrierten Rohkoordinaten
		const theta = Math.atan2(y0, x0); // rad
		// 2) Sollpunkt R=10 mm
		const x_soll = R_SOLL_MM * Math.cos(theta);
		const y_soll = R_SOLL_MM * Math.sin(theta);
		// 3) Korrektur addieren
		const x_adj = x_soll + r.dx; // (-DY)
		const y_adj = y_soll + r.dy; // ( DZ)
		// 4) zurück in Polar
		const r_adj = Math.hypot(x_adj, y_adj);
		// Winkeldefinition: 0° oben, CW
		const phi = Math.atan2(y_adj, x_adj);
		const ang0 = normAngleDeg(90 - phi * (180 / Math.PI)); // [0..360)
		// Für Plot: Bereich [180..540), Lücke später bei 540
		const x_plot = ang0 < 180 ? ang0 + 360 : ang0;

		series.push({
			name: r.pointName,
			x: x_plot,
			y: r_adj,
			dx: r.dx,
			dy: r.dy,
		});
	});
	// Sortieren nach Winkel (x) für saubere Linienführung
	series.sort((a, b) => a.x - b.x);

	// EINZIGE Lücke bei 180° -> Nullpunkt am Ende (x=540)
	series.push({ name: null, x: 540, y: null, dx: 0, dy: 0 });

	return {
		series,
		center: [cx, cy],
		rfit: rFit,
	};
}

function baseChartOptions(title: string, t: TFunction): Highcharts.Options {
	return {
		title: { text: title },
		chart: {
			polar: true,
			type: 'line',
			spacing: [4, 4, 4, 4], // außen wenig Abstand
			margin: [6, 6, 6, 6], // Plot-Margins klein
			height: Math.max((screen?.height || 0) / 3, 190),
		},
		pane: {
			startAngle: 180,
			endAngle: 540,
		},
		xAxis: {
			min: 180,
			max: 540,
			tickInterval: 30,
			labels: {
				formatter: function () {
					return `${Number(this.value || 0) % 360}°`;
				},
				distance: 8, // Polar-Labels näher an den Ring
			},
		},
		yAxis: {
			min: 0,
			title: { text: '' },
			labels: { enabled: false },
			tickLength: 0, // keine radialen Ticks
		},
		legend: {
			enabled: false,
		},
		tooltip: {
			useHTML: true,
			shared: false,
			headerFormat: '',
			formatter: function () {
				var p = this.point;
				if (p.y === null) return false;
				return (
					'<div style="font-size:12px">' +
					'<div><b>' +
					t('health.charts.point') +
					' ' +
					(p.name || '') +
					'</b></div>' +
					'<div>dX: ' +
					Highcharts.numberFormat(p.dx, 1) +
					' mm</div>' +
					'<div>dY: ' +
					Highcharts.numberFormat(p.dy, 1) +
					' mm</div>' +
					'</div>'
				);
			},
		},
	};
}

export type SectionChartsProps = {
	assetId: string;
};

export function SectionCharts({ assetId }: SectionChartsProps) {
	const { t } = useTranslation();
	const hasReadPermission = usePermission(ROLE.SENSORS_TPS_SECTION_READ);
	const { isDarkTheme } = useTheme();
	const groups: Record<string, { name: string; range: [number, number]; points: number[] }> = useMemo(
		() => ({
			'415': {
				name: t('health.charts.crossSection', { index: 1, from: 411, to: 415 }),
				range: [411, 415],
				points: [411, 412, 413, 414, 415],
			},
			'425': {
				name: t('health.charts.crossSection', { index: 2, from: 421, to: 425 }),
				range: [421, 425],
				points: [421, 422, 423, 424, 425],
			},
			'435': {
				name: t('health.charts.crossSection', { index: 3, from: 431, to: 435 }),
				range: [431, 435],
				points: [431, 432, 433, 434, 435],
			},
			'445': {
				name: t('health.charts.crossSection', { index: 4, from: 441, to: 445 }),
				range: [441, 445],
				points: [441, 442, 443, 444, 445],
			},
			'455': {
				name: t('health.charts.crossSection', { index: 5, from: 451, to: 455 }),
				range: [451, 455],
				points: [451, 452, 453, 454, 455],
			},
		}),
		[t],
	);
	const points = Object.keys(groups)
		.flatMap((key) => groups[key].points)
		.map(String);
	const { data: chartResponse, isLoading } = useQuery(useQueryGetTpsSectionChart(assetId, { points }));
	const chartData = chartResponse?.chartData || [];
	const chartFinalData = useMemo(() => {
		// get records by point for easy access
		const byPoint = chartData.reduce(
			(acc, record) => {
				const pointNum = Number(record.pointName);
				if (!acc[pointNum]) {
					acc[pointNum] = [];
				}
				const mappedRecord = {
					...record,
					x: record.x * 1000, // m -> mm
					y: record.y * 1000, // m -> mm
					dx: record.dx || 0.0,
					dy: record.dy || 0.0,
				};
				acc[pointNum].push(mappedRecord);
				return acc;
			},
			{} as Record<number, TpsSectionChartRecord[]>,
		);
		const targetRadiusLabel = t('health.charts.targetRadius');
		const excavationPitLabel = t('health.charts.excavationPit');
		return Object.keys(groups).map((groupKey) => {
			const groupData = groups[groupKey];
			const data = buildSeriesForGroup(byPoint, groupData.range[0], groupData.range[1]);
			return {
				key: groupKey,
				label: groupData.name,
				range: groupData.range,
				baseOptions: baseChartOptions(groupData.name, t),
				series: [
					{
						type: 'line',
						name: '',
						showInLegend: false,
						data: data.series, // enthält Nullpunkt bei x=540
						pointPlacement: 'on',
						marker: { enabled: true, radius: 3 },
						connectEnds: false,
					},
					{
						type: 'scatter',
						name: '',
						showInLegend: false,
						data: [[SOLL_ANGLE, SOLL_RADIUS]],
						marker: { enabled: false },
						enableMouseTracking: false,
						dataLabels: {
							enabled: true,
							format: targetRadiusLabel,
							align: 'center',
							y: 0,
							allowOverlap: true,
							crop: false,
							overflow: 'allow',
							style: { fontWeight: '600' },
						},
						zIndex: 10,
					},
					{
						type: 'scatter',
						name: '',
						showInLegend: false,
						data: [[BAU_ANGLE, BAU_RADIUS]],
						marker: { enabled: false },
						enableMouseTracking: false,
						dataLabels: {
							enabled: true,
							format: excavationPitLabel,
							rotation: 90,
							align: 'center',
							y: 20,
							allowOverlap: true,
							crop: false,
							overflow: 'allow',
							style: { fontWeight: '600' },
						},
						zIndex: 10,
					},
				],
			};
		});
	}, [chartData, groups, t]);
	const charts = useMemo(
		() =>
			chartFinalData.map((groupData) => ({
				...groupData,
				baseOptions: merge(
					structuredClone(isDarkTheme ? HIGH_CHART_DARK_THEME : HIGH_CHART_LIGHT_THEME),
					groupData.baseOptions,
				),
			})),
		[chartFinalData, isDarkTheme],
	);
	if (!hasReadPermission) {
		return <AccessDeniedView />;
	}
	if (isLoading) {
		return (
			<div className="h-50 relative w-full">
				<AbsoluteLoader className="bg-transparent" isLoading />
			</div>
		);
	}
	if (!chartData?.length) {
		return (
			<EmptyChart title={t('health.charts.noSectionDataTitle')} subtitle={t('health.charts.noSectionDataSubtitle')} />
		);
	}
	return (
		<div className="flex-1 overflow-auto p-6 m-0">
			<div className="space-y-3 @container">
				<div className="grid grid-cols-1 @xl:grid-cols-2 @3xl:grid-cols-3 gap-3">
					{charts.map((chart) => (
						<Card key={chart.key} className="border py-0">
							<CardContent className="py-1 px-1">
								<HighchartsReact
									highcharts={Highcharts}
									options={{
										...chart.baseOptions,
										series: chart.series,
									}}
								/>
							</CardContent>
						</Card>
					))}
				</div>
			</div>
		</div>
	);
}
